// The brand screen (spec/14 §14, second paragraph; F-HQ-38): `GET/PUT /v1/hq/brand`, five groups on
// the settings renderer (`brand-source.ts`), `If-Match` on every save. Part of Platform settings.
//
// What HQ does not set here is said once, above the groups: the product name, the domains, the apps
// and the sender are the deployment's and change with a deploy (spec/03 §12.1).

import type { HqBrandView } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { Badge } from '@clavian/ui-web';

import { useApi } from '../api/client';
import { queryKeys } from '../api/queries';
import type { Versioned } from '../api/versioned';
import { useSettingLabels } from '../settings/labels';
import { SettingsForm } from '../settings/SettingsForm';
import type { SettingLabels, SourceWords } from '../settings/types';
import { Loading } from '../shell/layouts';
import { LoadFailure } from '../shell/LoadFailure';
import { RequirePlatformPermission } from './access';
import { brandPart, fontFamily, readBrand, type BrandGroup } from './brand-source';
import { PlatformSettingsHeader } from './PlatformSettings';
import { HQ_SETTINGS } from './sections';
import { HqHeading } from './heading';

export function BrandPage(): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <RequirePlatformPermission permission={HQ_SETTINGS}>
      <div className="console-page">
        <HqHeading title={t('settings.title')} tip={t('brand.intro')} />
        <PlatformSettingsHeader current="brand" />
        <section className="console-settings__group" aria-labelledby="console-brand-title">
          {/* The tab above already says "Brand": the heading stays for a screen reader, not twice on screen. */}
          <h2 id="console-brand-title" className="clav-visually-hidden">
            {t('brand.title')}
          </h2>
          <BrandGroups />
        </section>
      </div>
    </RequirePlatformPermission>
  );
}

function BrandGroups() {
  const { t } = useTranslation('hq');
  const api = useApi();
  const brand = useQuery({ queryKey: queryKeys.hqBrand, queryFn: () => readBrand(api), refetchOnWindowFocus: false });
  if (brand.data === undefined) {
    return brand.isError ? <LoadFailure error={brand.error} message={t('brand.unavailable')} onRetry={() => void brand.refetch()} /> : <Loading />;
  }
  return <BrandForm view={brand.data} />;
}

function BrandForm({ view }: { readonly view: Versioned<HqBrandView> }) {
  const { t } = useTranslation('hq');
  const api = useApi();
  const queryClient = useQueryClient();
  const format = useVenueFormatter();
  const labels = useBrandLabels();
  const { t: consoleT } = useTranslation('console');

  const sources = useMemo<SourceWords>(
    () => ({
      // Only two sources here: head office's value, or the deployment's.
      badge: (source) => t(source === 'default' ? 'brand.source.default' : 'brand.source.platform'),
      name: (source) => t(source === 'default' ? 'brand.sourceName.default' : 'brand.sourceName.platform'),
      // A brand group's value is a set of colours and assets: the source line names where it comes from, not the value.
      using: (source) => t(source === 'default' ? 'brand.source.default' : 'brand.source.platform'),
      changed: t('brand.source.platform'),
      resetTo: (_source, value) => consoleT('settings.reset', { value }),
    }),
    [t, consoleT],
  );

  const part = useMemo(
    () =>
      brandPart({
        api,
        view,
        words: { label: (group: BrandGroup) => labels.field(group, []) },
        onVersion: (next) => queryClient.setQueryData(queryKeys.hqBrand, next),
      }),
    [api, view, labels, queryClient],
  );

  const { revision, updatedAt } = view.data;
  return (
    <>
      <p className="console-muted">
        {revision === 0 || updatedAt === undefined
          ? t('brand.revisionNone')
          : t('brand.revision', { revision, when: format.dateTime(updatedAt, { date: 'medium', time: 'short' }) })}
      </p>
      <BrandPreview theme={(view.data.values.theme ?? view.data.deployment.theme) as PreviewTheme | undefined} />
      <SettingsForm parts={[part]} labels={labels} sources={sources} />
    </>
  );
}

/** The brand's copy (`hq:brand.*`), and each font by its family's name. */
function useBrandLabels(): SettingLabels {
  const base = useSettingLabels(
    { entry: 'brand.group', field: 'brand.field', item: 'brand.item', description: 'brand.description', option: 'brand.option' },
    'hq',
  );
  return useMemo<SettingLabels>(
    () => ({
      ...base,
      option: (entryKey, path, value) =>
        entryKey === 'theme' && path.at(-2) === 'font' ? fontFamily(String(value)) : base.option(entryKey, path, value),
    }),
    [base],
  );
}

interface PreviewTheme {
  readonly colour?: { readonly primary?: string; readonly accent?: string };
  readonly surface?: 'dark' | 'light';
  readonly radius?: 'sharp' | 'soft' | 'round';
  readonly font?: { readonly display?: string; readonly body?: string };
}

const RADIUS: Readonly<Record<NonNullable<PreviewTheme['radius']>, string>> = {
  sharp: 'var(--clavian-radius-xs)',
  soft: 'var(--clavian-radius-md)',
  round: 'var(--clavian-radius-xl)',
};

/**
 * A live preview of the saved brand (spec/14 §14): a venue's lock-screen card drawn with its primary
 * and accent colours, its corners and its fonts. The colours are the brand's own runtime values —
 * data, not the console's palette — and fall back to the Clavian tokens where the brand sets none.
 */
function BrandPreview({ theme }: { readonly theme: PreviewTheme | undefined }): ReactNode {
  const { t } = useTranslation('hq');
  const style = {
    '--brand-primary': theme?.colour?.primary ?? 'var(--clavian-accent)',
    '--brand-accent': theme?.colour?.accent ?? 'var(--clavian-accent-ink)',
    '--brand-radius': RADIUS[theme?.radius ?? 'soft'],
    '--brand-display': theme?.font?.display === undefined ? 'var(--clavian-font-display)' : `"${fontFamily(theme.font.display)}", var(--clavian-font-display)`,
    '--brand-body': theme?.font?.body === undefined ? 'var(--clavian-font-body)' : `"${fontFamily(theme.font.body)}", var(--clavian-font-body)`,
  } as CSSProperties;
  return (
    <section className="console-brand-preview" aria-labelledby="console-brand-preview-title">
      <div className="console-brand-preview__head">
        <h3 id="console-brand-preview-title" className="console-brand-preview__title">
          {t('brand.preview.title')}
        </h3>
        <Badge tone="neutral">{t(`brand.preview.surface.${theme?.surface ?? 'dark'}`)}</Badge>
      </div>
      <div className="console-brand-preview__stage" style={style} aria-hidden="true">
        <div className="console-brand-preview__card">
          <span className="console-brand-preview__eyebrow">{t('brand.preview.eyebrow')}</span>
          <span className="console-brand-preview__heading">{t('brand.preview.heading')}</span>
          <span className="console-brand-preview__body">{t('brand.preview.body')}</span>
          <span className="console-brand-preview__button">{t('brand.preview.button')}</span>
          <span className="console-brand-preview__link">{t('brand.preview.link')}</span>
        </div>
      </div>
      <p className="console-subtle">{t('brand.preview.hint')}</p>
    </section>
  );
}
